import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Button from './Button';
import useResponsive from '../hooks/useResponsive';

// VI-710: Jobseeker survey banner shown on the "Interview Successfully Completed"
// page. The survey lives in SurveyMonkey; per the ticket we do NOT collect or
// store any PII on our side — SurveyMonkey tracks completions and the gift-card
// fulfilment is handled manually. Display is gated by the Statsig feature gate
// `vi_jobseeker_survey_banner` (checked by the parent, Confirmation.tsx), which
// is switched off once the survey reaches its response cap.
const SURVEY_URL = 'https://www.surveymonkey.com/r/89QJGVK';

// Colors are taken directly from the Figma "Approved Design" (node 2649:1907).
// The app has no design-token system, so literals are the convention here.
const COLORS = {
  contentBg: '#f4f5f9',
  giftBg: '#ebf4ff',
  primary: '#2f86dd',
  title: '#020305',
  body: '#272a2f',
};

const SurveyBanner = () => {
  // useResponsive('md') is true below 831px — matches the mobile breakpoint used
  // across the applicant flow (Interview.tsx, analytics is_mobile, etc.).
  const isMobile = useResponsive('md');

  // The button carries id="btn-sharefeedback-click" so GTM can fire on the click
  // (same convention as the "Register as a Job Seeker" button). We deliberately
  // push no dataLayer payload / PII — SurveyMonkey owns completion tracking.
  const handleShareFeedback = () => {
    window.open(SURVEY_URL, '_blank', 'noopener,noreferrer');
  };

  // The gift graphic: on desktop it's the tall right-hand column (icon over "$10"
  // over "Gift Card"); on mobile it collapses to a compact full-width strip that
  // sits on TOP of the copy (icon · $10 · Gift Card, inline).
  const giftPanel = (
    <div
      style={{
        backgroundColor: COLORS.giftBg,
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Desktop uses 0 gap and lets the icon box + "$10" line-height create the
        // vertical rhythm (matches Figma, which QA flagged as too tight in QA).
        gap: isMobile ? '10px' : '0px',
        padding: isMobile ? '12px 16px' : '24px',
        width: isMobile ? '100%' : '183px',
        flexShrink: 0,
      }}
    >
      {/* Outlined "gift-card" icon (FA5 Pro `far`, loaded globally via index.html).
          On desktop the 32px glyph is centered in a 48px box to match Figma's
          frame, giving the icon the same breathing room as the design. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: isMobile ? 'auto' : '48px',
        }}
      >
        <i
          className="far fa-gift-card"
          style={{ fontSize: isMobile ? '20px' : '32px', color: COLORS.primary }}
        />
      </div>
      <span
        style={{
          fontSize: isMobile ? '22px' : '48px',
          fontWeight: 600,
          // Figma "$10" sits in a ~72px line box (1.5×) on desktop for extra
          // vertical spacing, and uses tight -4.32px (≈ -0.09em) tracking.
          lineHeight: isMobile ? 1 : 1.5,
          letterSpacing: '-0.09em',
          color: '#000000',
        }}
      >
        $10
      </span>
      <span
        style={{
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '24px',
          color: '#000000',
        }}
      >
        Gift Card
      </span>
    </div>
  );

  const contentPanel = (
    <div
      style={{
        backgroundColor: COLORS.contentBg,
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '24px',
        padding: '24px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', textAlign: 'left' }}>
        <p
          style={{
            margin: 0,
            fontSize: '23px',
            fontWeight: 600,
            lineHeight: '32px',
            color: COLORS.title,
          }}
        >
          We&rsquo;d Love To Hear About Your Interview Experience
        </p>
        <p style={{ margin: 0, fontSize: '16px', lineHeight: '24px', color: COLORS.body }}>
          Complete our survey and receive a <strong style={{ fontWeight: 600 }}>$10 Gift Card</strong>. No login
          required. Your feedback helps improve the experience for future candidates.
        </p>
      </div>
      <Button
        variant="primary"
        showSpinner={false}
        id="btn-sharefeedback-click"
        onClick={handleShareFeedback}
        // Figma button is 40px tall with 12px horizontal padding; the shared
        // DS button renders a little smaller, so nudge it to match the design.
        style={{ height: '40px', width: isMobile ? '100%' : '187px', padding: '8px 12px' }}
        text={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            Share My Feedback
            <FontAwesomeIcon icon={faArrowRight} />
          </span>
        }
      />
    </div>
  );

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '1140px',
        // Extra breathing room below the banner before the success block (Figma ~64px).
        margin: '0 auto 4rem',
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        // column-reverse puts the gift panel (rendered 2nd) on top for mobile.
        flexDirection: isMobile ? 'column-reverse' : 'row',
        alignItems: 'stretch',
      }}
    >
      {contentPanel}
      {giftPanel}
    </div>
  );
};

export default SurveyBanner;
